import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const comic = useMemo(() => comics.find((c) => c.slug === slug), [slug]);
  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-2xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i> 返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    genre: comic.tags,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '5',
      ratingCount: Math.floor(comic.views / 100),
    },
    image: comic.coverImage,
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${comic.title}多久更新一次？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}目前状态为${comic.status}，更新频率稳定，请关注073漫画网获取最新章节通知。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}可以在哪里免费阅读？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}全集可在073漫画网免费在线阅读，无需注册即可畅享所有章节。`,
        },
      },
      {
        '@type': 'Question',
        name: `${comic.title}是什么类型的漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${comic.title}属于${comic.tags.join('、')}类型的韩国漫画，由${comic.author}创作，深受读者喜爱。`,
        },
      },
    ],
  };

  const catName = categories.find((c) => c.slug === comic.categorySlug)?.name || comic.category;

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(comicSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${catName}漫画`, href: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]}
        />

        {/* Comic Detail */}
        <article className="mb-10">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 w-full md:w-[280px]">
              <div className="aspect-[5/7] rounded-lg overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - 073漫画网韩国漫画免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-500 whitespace-nowrap">作者：{comic.author}</span>
                <span className="text-foreground-300 text-sm">|</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">分类：{catName}</span>
                <span className="text-foreground-300 text-sm">|</span>
                <span className={`text-sm px-2 py-0.5 rounded-md whitespace-nowrap ${comic.status === '已完结' ? 'bg-background-200/70 text-foreground-500' : 'bg-secondary-100 text-secondary-900'}`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-5">
                <div className="flex items-center gap-1.5">
                  <span className="text-accent-500 font-bold text-lg">{comic.rating}</span>
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="text-foreground-500 text-sm">评分</span>
                </div>
                <span className="text-foreground-300">|</span>
                <div className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-foreground-500"></i>
                  <span className="text-foreground-500 text-sm">
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 次阅读
                  </span>
                </div>
                <span className="text-foreground-300">|</span>
                <div className="flex items-center gap-1.5">
                  <i className="ri-file-list-3-line text-foreground-500"></i>
                  <span className="text-foreground-500 text-sm">共 {comic.chapters.length} 话</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-primary-100 text-primary-700 px-3 py-1 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              <Link
                to={`/article/${comic.slug}`}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i> 开始阅读
              </Link>
            </div>
          </div>

          {/* Chapter List */}
          <section className="mt-10">
            <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              章节列表
            </h2>
            <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
              <div className="divide-y divide-background-200/50 max-h-[500px] overflow-y-auto">
                {[...comic.chapters].reverse().map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/article/${comic.slug}`}
                    className="flex items-center justify-between px-5 py-3 hover:bg-background-100 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      {ch.isNew && (
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                      )}
                      <span className="text-sm text-foreground-950 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                        {ch.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateDate}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </article>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="mb-14">
            <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {relatedComics.map((rc) => (
                <ComicCard key={rc.id} comic={rc} size="small" />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}